import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Phone, Mail, FileText, Clock, PhoneCall, Sparkles, MessageSquare } from "lucide-react";
import { APPLY } from "@/constants/testIds";
import { trackEvent } from "@/lib/analytics";
import { CONTACT } from "@/data/content";
import Seo from "@/lib/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "business", label: "Business" },
  { key: "revenue", label: "Revenue" },
  { key: "funding", label: "Funding" },
  { key: "product", label: "Product" },
  { key: "review", label: "Review" },
];

const initial = {
  first_name: "",
  last_name: "",
  email: "",
  business_name: "",
  mobile_phone: "",
  direct_phone: "",
  business_phone: "",
  business_type: "",
  time_in_business: "",
  state: "",
  monthly_revenue: "",
  credit_score_range: "",
  funding_amount: "",
  funding_purpose: "",
  product_type: "",
  notes: "",
};

const Field = ({ label, children }) => (
  <div>
    <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
      {label}
    </label>
    {children}
  </div>
);

export default function Apply() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [smsSaving, setSmsSaving] = useState(false);
  const [smsSaved, setSmsSaved] = useState(false);
  const navigate = useNavigate();

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const stepValid = () => {
    if (step === 0)
      return (
        form.first_name &&
        form.last_name &&
        form.email &&
        form.business_name &&
        form.mobile_phone &&
        form.business_type &&
        form.time_in_business &&
        form.state
      );
    if (step === 1) return form.monthly_revenue && form.credit_score_range;
    if (step === 2) return form.funding_amount && form.funding_purpose;
    if (step === 3) return form.product_type;
    return true;
  };

  const next = () => {
    if (!stepValid()) {
      toast.error("Please complete every field before continuing.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/applications`, form);
      trackEvent("application_submitted", {
        product_type: form.product_type,
        funding_amount: form.funding_amount,
        time_in_business: form.time_in_business,
        state: form.state,
        application_id: res.data?.id,
      });
      setApplicationId(res.data?.id || "");
      setSubmitted(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      trackEvent("application_error", { message: err?.message || "unknown" });
      toast.error("Something went wrong. Please try again or call 888.275.0098.");
    } finally {
      setLoading(false);
    }
  };

  // Short human-friendly reference derived from the UUID application id.
  const referenceCode = applicationId
    ? `SPF-${applicationId.slice(0, 4).toUpperCase()}-${applicationId.slice(-4).toUpperCase()}`
    : "SPF-PENDING";

  const enableSms = async () => {
    if (!smsConsent || !applicationId || !form.mobile_phone) return;
    setSmsSaving(true);
    try {
      await axios.post(`${API}/applications/${applicationId}/sms-optin`, {
        phone: form.mobile_phone,
        consent: true,
      });
      trackEvent("sms_optin_enabled", { application_id: applicationId });
      setSmsSaved(true);
    } catch (err) {
      toast.error("Couldn't save your text opt-in. Please try again.");
    } finally {
      setSmsSaving(false);
    }
  };

  // 3-step timeline shown after submission.
  const NEXT_STEPS = [
    {
      icon: PhoneCall,
      when: "Within 24 hours · Mon–Fri",
      title: "Broker introduction call",
      body:
        "A funding specialist calls to confirm your file, answer questions, and walk through which lenders fit best. Keep your phone nearby.",
    },
    {
      icon: FileText,
      when: "Within 24 hours",
      title: "Document collection",
      body:
        "We'll request the exact documents needed — usually just 4–6 months of business bank statements plus one form of ID. Sent via a secure upload link.",
    },
    {
      icon: Sparkles,
      when: "3–5 business days",
      title: "Real offers, side by side",
      body:
        "Multiple lender offers presented in one plain-English comparison. You choose. No pressure, no fees to shop.",
    },
  ];

  // Documents to have ready for the broker call.
  const DOCS = [
    "Last 4–6 months of business bank statements (PDF)",
    "Driver's license or government-issued ID",
    "Voided business check or bank letter (for funding)",
    "Most recent business tax return (SBA / Term loans only)",
  ];

  if (submitted) {
    return (
      <div>
        <Seo path="/apply" title="Application Received" noindex />

        {/* Hero */}
        <section className="pt-36 pb-16 border-b border-black/10">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, ease: [0.7, 0, 0.2, 1] }}
              className="w-16 h-16 flex items-center justify-center border border-[color:var(--gold)] text-[color:var(--gold)]"
              data-testid="apply-thank-you-check"
            >
              <Check size={28} />
            </motion.div>

            <div className="mt-10 grid grid-cols-12 gap-8 items-end">
              <div className="col-span-12 md:col-span-8">
                <div className="overline">Application received · Thank you</div>
                <h1 className="mt-6 font-serif text-6xl md:text-8xl leading-[0.9] tracking-tight">
                  You're <em className="italic text-[color:var(--gold)]">on the calendar</em>.
                </h1>
                <div className="mt-8 h-px w-12 bg-[color:var(--gold)]" />
                <p className="mt-8 max-w-2xl text-[color:var(--bone)]/75 leading-relaxed text-lg">
                  A funding specialist has been alerted and will reach out within 24 hours during business hours (Monday–Friday). No credit was pulled. Nothing is committed. Here's exactly what happens from here.
                </p>
              </div>
              <div className="col-span-12 md:col-span-4">
                <div className="border border-black/10 bg-[color:var(--bg-elev)] p-6" data-testid="apply-reference-card">
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/55">
                    Your reference
                  </div>
                  <div className="mt-3 font-mono text-2xl tracking-[0.18em] text-[color:var(--gold)]">
                    {referenceCode}
                  </div>
                  <div className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--bone)]/45">
                    Keep this handy — it identifies your file with any broker on our team.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-20 md:py-28 border-b border-black/10">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <div className="overline mb-8">What happens next · 3 steps</div>
            <div className="space-y-0" data-testid="apply-timeline">
              {NEXT_STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <motion.div
                    key={s.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: i * 0.12 }}
                    className="grid grid-cols-12 gap-6 py-10 border-t border-black/10 first:border-t-0"
                  >
                    <div className="col-span-2 md:col-span-1 flex flex-col items-center">
                      <div className="w-11 h-11 flex items-center justify-center border border-[color:var(--gold)] text-[color:var(--gold)] flex-shrink-0">
                        <Icon size={17} />
                      </div>
                      {i < NEXT_STEPS.length - 1 && (
                        <div className="mt-3 flex-1 w-px bg-[color:var(--gold)]/25 min-h-[40px]" />
                      )}
                    </div>
                    <div className="col-span-10 md:col-span-11">
                      <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--gold)]">
                        <Clock size={11} /> {s.when}
                      </div>
                      <h3 className="mt-3 font-serif text-3xl md:text-4xl leading-tight">
                        {s.title}
                      </h3>
                      <p className="mt-4 text-[color:var(--bone)]/75 leading-relaxed max-w-2xl">
                        {s.body}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* SMS opt-in — nudge me when offers land */}
        <section className="py-20 md:py-28 border-b border-black/10">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-12 gap-10">
              <div className="col-span-12 md:col-span-5">
                <div className="overline">One more thing · Optional</div>
                <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1]">
                  Text me when <em className="italic text-[color:var(--gold)]">offers land</em>.
                </h2>
                <p className="mt-6 text-[color:var(--bone)]/70 leading-relaxed max-w-md">
                  Lender offers sometimes arrive overnight or on a weekend. If you'd rather get a quick text than wait for a callback, opt in below — a broker will text you the moment there's something worth reading.
                </p>
              </div>
              <div className="col-span-12 md:col-span-7">
                <div
                  className="border border-[color:var(--gold)]/30 bg-[color:var(--bg-elev)] p-6 md:p-8"
                  data-testid="apply-sms-optin"
                >
                  {smsSaved ? (
                    <div className="flex items-start gap-4" data-testid="apply-sms-confirmed">
                      <div className="mt-1 w-8 h-8 flex items-center justify-center border border-[color:var(--gold)] text-[color:var(--gold)] flex-shrink-0">
                        <Check size={14} />
                      </div>
                      <div>
                        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--gold)]">
                          Text updates enabled
                        </div>
                        <p className="mt-3 text-[color:var(--bone)]/80 leading-relaxed">
                          We'll text {form.mobile_phone || "your phone"} the moment your file has offers to review. Reply STOP any time to opt out — no questions asked.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--gold)]">
                        <MessageSquare size={12} /> Broker text updates
                      </div>
                      <div className="mt-5">
                        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
                          Text me at
                        </div>
                        <div className="mt-2 font-mono text-2xl tracking-[0.08em] text-[color:var(--bone)]" data-testid="apply-sms-phone">
                          {form.mobile_phone || "—"}
                        </div>
                      </div>
                      <label className="mt-6 flex items-start gap-3 cursor-pointer group" data-testid="apply-sms-consent-label">
                        <input
                          type="checkbox"
                          checked={smsConsent}
                          onChange={(e) => setSmsConsent(e.target.checked)}
                          data-testid="apply-sms-consent"
                          className="mt-1 w-4 h-4 accent-[color:var(--gold)] flex-shrink-0"
                        />
                        <span className="text-sm text-[color:var(--bone)]/75 leading-relaxed">
                          I agree to receive text messages from Steady Path Funding about my funding application. Message and data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help. Consent is not required to receive funding.
                        </span>
                      </label>
                      <button
                        type="button"
                        onClick={enableSms}
                        disabled={!smsConsent || smsSaving || !form.mobile_phone}
                        data-testid="apply-sms-enable"
                        className="mt-6 inline-flex items-center gap-3 border border-[color:var(--gold)] text-[color:var(--gold)] px-6 py-3 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[color:var(--gold)]"
                      >
                        {smsSaving ? "Saving…" : "Enable text updates"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Document prep */}
        <section className="py-20 md:py-28 border-b border-black/10 bg-[color:var(--surface)]">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-5">
              <div className="overline">Meanwhile · Two minutes of prep</div>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1]">
                Have these <em className="italic text-[color:var(--gold)]">ready</em>.
              </h2>
              <p className="mt-6 text-[color:var(--bone)]/70 leading-relaxed">
                Every file is different, but 95% of applications need only what's on this list. Gathering them now shaves days off the timeline.
              </p>
            </div>
            <div className="col-span-12 md:col-span-7">
              <ul className="space-y-3" data-testid="apply-doc-checklist">
                {DOCS.map((d) => (
                  <li
                    key={d}
                    className="flex items-start gap-4 border-b border-black/10 pb-4 last:border-b-0"
                  >
                    <div className="mt-1 w-5 h-5 flex items-center justify-center border border-[color:var(--gold)]/50 text-[color:var(--gold)] flex-shrink-0">
                      <Check size={11} />
                    </div>
                    <span className="text-[color:var(--bone)]/85 leading-relaxed">{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Contact + navigation */}
        <section className="py-20 md:py-28">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-12 gap-10">
              <div className="col-span-12 md:col-span-7">
                <div className="overline">Need us before we call?</div>
                <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1]">
                  Direct line to a broker.
                </h2>
                <p className="mt-6 text-[color:var(--bone)]/70 leading-relaxed max-w-lg">
                  If timing is urgent or you have a question before the callback, reach us directly. Mention your reference code and any broker on the team can pull your file.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-6">
                  <a
                    href={`tel:${CONTACT.mainPhone.replace(/[^\d]/g, "")}`}
                    data-testid="apply-contact-phone"
                    className="inline-flex items-center gap-3 border border-[color:var(--gold)] text-[color:var(--gold)] px-6 py-3 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold)] hover:text-black transition-colors"
                  >
                    <Phone size={14} /> {CONTACT.mainPhone}
                  </a>
                  <a
                    href={`mailto:${CONTACT.email}?subject=Reference ${referenceCode}`}
                    data-testid="apply-contact-email"
                    className="inline-flex items-center gap-3 font-mono text-xs tracking-[0.24em] uppercase gold-link text-[color:var(--bone)]/80"
                  >
                    <Mail size={14} /> {CONTACT.email}
                  </a>
                </div>
              </div>
              <div className="col-span-12 md:col-span-5 md:pl-8 md:border-l border-black/10">
                <div className="overline">While you wait</div>
                <h3 className="mt-4 font-serif text-2xl md:text-3xl leading-tight">
                  Read what's coming.
                </h3>
                <p className="mt-4 text-[color:var(--bone)]/70 leading-relaxed">
                  These field notes explain the process from here — what the broker will ask and how to read the offers when they land.
                </p>
                <div className="mt-6 space-y-3">
                  <Link
                    to="/blog/what-happens-after-you-prequalify"
                    data-testid="apply-thank-you-related-1"
                    className="group flex items-baseline justify-between gap-3 py-3 border-t border-black/10 hover:border-[color:var(--gold)]/40 transition-colors"
                  >
                    <span className="font-serif text-lg group-hover:text-[color:var(--gold)] transition-colors">
                      What happens after you prequalify
                    </span>
                    <ArrowRight size={14} className="text-[color:var(--gold)] flex-shrink-0" />
                  </Link>
                  <Link
                    to="/blog/prepare-business-loan-application-2026"
                    data-testid="apply-thank-you-related-2"
                    className="group flex items-baseline justify-between gap-3 py-3 border-t border-black/10 hover:border-[color:var(--gold)]/40 transition-colors"
                  >
                    <span className="font-serif text-lg group-hover:text-[color:var(--gold)] transition-colors">
                      How to prepare for a business loan application
                    </span>
                    <ArrowRight size={14} className="text-[color:var(--gold)] flex-shrink-0" />
                  </Link>
                </div>
              </div>
            </div>

            <div className="mt-16 pt-8 border-t border-black/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/45">
                Reference · {referenceCode}
              </div>
              <button
                onClick={() => navigate("/")}
                data-testid="apply-thank-you-home"
                className="font-mono text-xs tracking-[0.24em] uppercase gold-link text-[color:var(--gold)]"
              >
                Return home →
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pt-40 pb-32">
      <Seo
        path="/apply"
        title="Apply for Business Funding"
        description="Four-minute application. No credit pull. A broker will call you within 24 hours during business hours (Mon–Fri)."
      />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-12">
        {/* Stepper */}
        <div className="col-span-12 md:col-span-4">
          <div className="overline">Application · {String(step + 1).padStart(2, "0")} / 05</div>
          <h1 className="mt-6 font-serif text-5xl md:text-6xl leading-[0.95]">
            Four minutes. <em className="italic text-[color:var(--gold)]">No credit pull</em>.
          </h1>
          <p className="mt-6 text-[color:var(--bone)]/70 leading-relaxed max-w-sm">
            No minimum FICO. No merchant processor statements. Just 4–6 months
            of business bank statements. Broker callback within 24 hours (Mon–Fri).
          </p>
          <ol className="mt-12 space-y-4" data-testid={APPLY.stepIndicator}>
            {STEPS.map((s, i) => (
              <li key={s.key} className="flex items-center gap-4 font-mono text-xs tracking-[0.24em] uppercase">
                <span
                  className={`w-6 h-6 flex items-center justify-center border ${
                    i < step
                      ? "border-[color:var(--gold)] bg-[color:var(--gold)] text-black"
                      : i === step
                      ? "border-[color:var(--gold)] text-[color:var(--gold)]"
                      : "border-black/20 text-[color:var(--bone)]/40"
                  }`}
                >
                  {i < step ? <Check size={12} /> : String(i + 1).padStart(2, "0")}
                </span>
                <span className={i === step ? "text-[color:var(--gold)]" : i < step ? "text-[color:var(--bone)]/80" : "text-[color:var(--bone)]/40"}>
                  {s.label}
                </span>
              </li>
            ))}
          </ol>
        </div>

        {/* Form */}
        <div className="col-span-12 md:col-span-8" data-testid={APPLY.form}>
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.45, ease: [0.7, 0, 0.2, 1] }}
              className="border border-black/10 bg-[color:var(--bg-elev)] p-8 md:p-12"
            >
              {step === 0 && (
                <div className="space-y-6">
                  <h2 className="font-serif text-3xl md:text-4xl">Tell us about your business</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    <Field label="First Name">
                      <input className="editorial-input" value={form.first_name} onChange={change("first_name")} placeholder="Jane" />
                    </Field>
                    <Field label="Last Name">
                      <input className="editorial-input" value={form.last_name} onChange={change("last_name")} placeholder="Doe" />
                    </Field>
                    <Field label="Email">
                      <input type="email" className="editorial-input" value={form.email} onChange={change("email")} placeholder="you@business.com" />
                    </Field>
                    <Field label="Business Name">
                      <input className="editorial-input" value={form.business_name} onChange={change("business_name")} placeholder="Acme LLC" />
                    </Field>
                    <Field label="Mobile Number">
                      <input className="editorial-input" value={form.mobile_phone} onChange={change("mobile_phone")} placeholder="(708) 555-0110" />
                    </Field>
                    <Field label="Direct Number (optional)">
                      <input className="editorial-input" value={form.direct_phone} onChange={change("direct_phone")} placeholder="(708) 555-0111" />
                    </Field>
                    <Field label="Business Number (optional)">
                      <input className="editorial-input" value={form.business_phone} onChange={change("business_phone")} placeholder="(708) 555-0112" />
                    </Field>
                    <Field label="Business Type">
                      <select className="editorial-select mt-2" value={form.business_type} onChange={change("business_type")}>
                        <option value="">Select…</option>
                        <option>LLC</option><option>C-Corp</option><option>S-Corp</option><option>Sole Proprietor</option><option>Partnership</option><option>Non-Profit</option>
                      </select>
                    </Field>
                    <Field label="Time in Business">
                      <select className="editorial-select mt-2" value={form.time_in_business} onChange={change("time_in_business")}>
                        <option value="">Select…</option>
                        <option>Less than 6 months</option><option>6–12 months</option><option>1–2 years</option><option>2–5 years</option><option>5+ years</option>
                      </select>
                    </Field>
                    <Field label="State of Operation">
                      <input className="editorial-input" value={form.state} onChange={change("state")} placeholder="IL" />
                    </Field>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-6">
                  <h2 className="font-serif text-3xl md:text-4xl">Revenue & credit</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    <Field label="Average Monthly Revenue">
                      <select className="editorial-select mt-2" value={form.monthly_revenue} onChange={change("monthly_revenue")}>
                        <option value="">Select…</option>
                        <option>Under $10K</option>
                        <option>$10K – $15K</option>
                        <option>$15K – $20K</option>
                        <option>$20K – $30K</option>
                        <option>$30K – $50K</option>
                        <option>$50K – $75K</option>
                        <option>$75K – $100K</option>
                        <option>$100K+</option>
                      </select>
                    </Field>
                    <Field label="Credit Score Range">
                      <select className="editorial-select mt-2" value={form.credit_score_range} onChange={change("credit_score_range")}>
                        <option value="">Select…</option>
                        <option>Below 550</option><option>550 – 619</option><option>620 – 679</option><option>680 – 719</option><option>720+</option>
                      </select>
                    </Field>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6">
                  <h2 className="font-serif text-3xl md:text-4xl">How much & what for</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    <Field label="Funding Amount Needed">
                      <select className="editorial-select mt-2" value={form.funding_amount} onChange={change("funding_amount")}>
                        <option value="">Select…</option>
                        <option>Under $50,000</option><option>$50,000 – $150,000</option><option>$150,000 – $500,000</option><option>$500,000 – $1M</option><option>$1M – $5M</option><option>$5M+</option>
                      </select>
                    </Field>
                    <Field label="Use of Funds">
                      <select className="editorial-select mt-2" value={form.funding_purpose} onChange={change("funding_purpose")}>
                        <option value="">Select…</option>
                        <option>Working Capital</option><option>Equipment</option><option>Inventory</option><option>Payroll</option><option>Marketing / Growth</option><option>Real Estate</option><option>Debt Refinance</option><option>Other</option>
                      </select>
                    </Field>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6">
                  <h2 className="font-serif text-3xl md:text-4xl">Preferred product</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {["SBA Loan", "Business Line of Credit", "Revenue Based Financing", "Equipment Financing", "Working Capital", "Merchant Cash Advance", "Term Loan", "Bridge Loan", "Startup Financing", "Fix and Flip Financing", "Purchase Order Financing", "Accounts Receivable Financing", "Franchise Financing", "Residential Investment Financing", "Not sure — you decide"].map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, product_type: p }))}
                        className={`text-left p-5 border transition-all duration-300 ${
                          form.product_type === p
                            ? "border-[color:var(--gold)] bg-[color:var(--gold)]/10 text-[color:var(--gold)]"
                            : "border-black/15 text-[color:var(--bone)]/85 hover:border-black/40"
                        }`}
                      >
                        <div className="font-serif text-xl">{p}</div>
                      </button>
                    ))}
                  </div>
                  <Field label="Anything else we should know?">
                    <textarea rows={4} className="editorial-input resize-none" value={form.notes} onChange={change("notes")} placeholder="Optional — share context or timing constraints." />
                  </Field>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-6">
                  <h2 className="font-serif text-3xl md:text-4xl">Review & submit</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                    {Object.entries(form).map(([k, v]) => (
                      <div key={k} className="border-b border-black/10 py-2">
                        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
                          {k.replace(/_/g, " ")}
                        </div>
                        <div className="text-[color:var(--bone)] mt-1">{v || "—"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-10 flex items-center justify-between">
                <button
                  data-testid={APPLY.back}
                  type="button"
                  onClick={back}
                  disabled={step === 0}
                  className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--bone)]/70 hover:text-[color:var(--bone)] disabled:opacity-30"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                {step < STEPS.length - 1 ? (
                  <button
                    data-testid={APPLY.next}
                    type="button"
                    onClick={next}
                    className="inline-flex items-center gap-3 bg-[color:var(--gold)] text-black px-8 py-3.5 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold-strong)] transition-colors"
                  >
                    Continue <ArrowRight size={14} />
                  </button>
                ) : (
                  <button
                    data-testid={APPLY.submit}
                    type="button"
                    onClick={submit}
                    disabled={loading}
                    className="inline-flex items-center gap-3 bg-[color:var(--gold)] text-black px-8 py-3.5 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold-strong)] transition-colors disabled:opacity-60"
                  >
                    {loading ? "Submitting…" : "Submit Application"}
                    <ArrowRight size={14} />
                  </button>
                )}
              </div>
              {step === STEPS.length - 1 && (
                <p
                  data-testid="apply-consent"
                  className="mt-6 text-xs text-[color:var(--bone)]/55 leading-relaxed max-w-2xl ml-auto text-right"
                >
                  By submitting, you agree to our{" "}
                  <Link to="/terms" className="text-[color:var(--gold)] border-b border-[color:var(--gold)]/40 hover:border-[color:var(--gold)]">
                    Terms
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="text-[color:var(--gold)] border-b border-[color:var(--gold)]/40 hover:border-[color:var(--gold)]">
                    Privacy Policy
                  </Link>
                  . You authorize Steady Path Funding and our lender partners to contact you at the number and email provided.
                </p>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
